/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDInput from "components/MDInput";

// Material-UI components
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import apiClient from "./../../../../apiClient";
import DataTable from "examples/Tables/DataTable";

const FetchStateData = ({ onRefresh, editData }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateInput, setStateInput] = useState("");
  const [statusInput, setStatusInput] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [darkMode, setDarkMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    onConfirm: null,
  });

  const handleStateInputChange = (e) => {
    setStateInput(e.target.value);
  };

  const handleStatusInputChange = (e) => {
    setStatusInput(e.target.value);
  };

  const fetchStates = async (page = currentPage) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/admin/state?page=${page}`);
      response.data.sort((a, b) => a.name.localeCompare(b.name));
      setData(response.data);
      setCurrentPage(page); // Update current page
    } catch (error) {
      console.error("Error fetching state data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchStates(newPage); // Fetch data for the new page
  };

  useEffect(() => {
    fetchStates();
  }, [onRefresh]);

  // Handlers
  const handleActivate = async (id, status) => {
    try {
      setConfirmDialog({
        open: true,
        title:
          "Are you sure you want to " +
          (status === "Active" ? "deactivate" : "activate") +
          " this state?",
        onConfirm: async () => {
          try {
            await apiClient.put(`/admin/state/${id}`, { status: status });
            await fetchStates(currentPage); // Refresh data while staying on the current page
            // Keep current page after refresh
          } catch (error) {
            console.error("Error updating verification status:", error);
          }
        },
      });
    } catch (error) {
      console.error("Error activating state:", error);
    }
  };

  const handleDelete = async (id, name) => {
    try {
      setConfirmDialog({
        open: true,
        title: `Are you sure you want to delete the state "${name}"?`,
        onConfirm: async () => {
          try {
            await apiClient.delete(`/admin/state/${id}`);
            fetchStates(); // Refresh data after delete
          } catch (error) {
            console.error("Error deleting state:", error);
          }
        },
      });
    } catch (error) {
      console.error("Error deleting state:", error);
    }
  };

  const handleEdit = (data) => {
    // Handle edit functionality
    console.log("Edit state with ID:", data);
    editData(data);
  };

  const handleFilterSubmit = () => {
    setStateFilter(stateInput);
    setStatusFilter(statusInput);
  };

  // Filter the rows based on search term
  const filteredRows = data.filter((row) => {
    return (
      (stateFilter === "" || row.name.toLowerCase().includes(stateFilter.toLowerCase())) &&
      (statusFilter === "" || row.status.toLowerCase().includes(statusFilter.toLowerCase()))
    );
  });

  // Table columns and rows
  const columns = [
    { Header: "State Name", accessor: "name", width: "25%", align: "left" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Action", accessor: "action", align: "center" },
  ];

  const rows = filteredRows.map((item) => ({
    name: (
      <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
        {item.name}
      </MDTypography>
    ),
    status: (
      <MDBox ml={-1}>
        <MDBadge
          badgeContent={item.status}
          color={item.status === "Active" ? "success" : "warning"}
          variant="gradient"
          size="sm"
        />
      </MDBox>
    ),
    action: (
      <MDBox display="flex" justifyContent="space-around">
        <IconButton onClick={() => handleEdit(item)} color="secondary">
          <EditIcon />
        </IconButton>
        <IconButton
          onClick={() => handleActivate(item._id, item.status)}
          color={item.status === "Active" ? "warning" : "success"}
        >
          <CheckCircleIcon />
        </IconButton>
        <IconButton onClick={() => handleDelete(item._id, item.name)} color="error">
          <DeleteIcon />
        </IconButton>
      </MDBox>
    ),
  }));

  // No data message
  if (loading) return <div>Loading...</div>;
  if (data.length === 0) return <div>No data found.</div>;

  return (
    <div>
      <MDBox
        display="flex"
        gap="1rem"
        mb="1rem"
        justifyContent="left"
        alignItems="left"
        ml="2%"
        width="50%"
      >
        <MDInput
          fullWidth
          label="State Name"
          variant="outlined"
          value={stateInput}
          onChange={handleStateInputChange}
        />
        <Select
          fullWidth
          variant="outlined"
          value={statusInput}
          onChange={handleStatusInputChange}
          displayEmpty
        >
          <MenuItem value="">
            <em>Status</em>
          </MenuItem>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Inactive">Inactive</MenuItem>
        </Select>
        <Button
          variant="contained"
          color="primary"
          onClick={handleFilterSubmit}
          sx={{ color: "#fff" }}
        >
          Search
        </Button>
      </MDBox>
      <Divider />
      <DataTable
        table={{ columns, rows }}
        isSorted={true}
        entriesPerPage={true}
        showTotalEntries={true}
        noEndBorder
        onPageChange={handlePageChange} // Pass the handler here
      />
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: darkMode ? "#121212" : "#fff",
            color: darkMode ? "#fff" : "#000",
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1300,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: darkMode ? "#121212" : "#fff",
            color: darkMode ? "#fff" : "#000",
            borderBottom: `1px solid ${darkMode ? "#333" : "#e0e0e0"}`,
          }}
        >
          {confirmDialog.title}
        </DialogTitle>
        <DialogActions
          sx={{
            backgroundColor: darkMode ? "#121212" : "#fff",
            padding: "16px 24px",
          }}
        >
          <Button
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="inherit"
            sx={{ color: darkMode ? "#ccc" : "#666" }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (confirmDialog.onConfirm) {
                await confirmDialog.onConfirm();
              }
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            variant="contained"
            color="primary"
            sx={{ color: "#fff", ml: 1 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
FetchStateData.propTypes = {
  onRefresh: PropTypes.func.isRequired,
  editData: PropTypes.func.isRequired,
};

export default FetchStateData;
